import React, { useState, useEffect, useRef } from 'react';
import { createUseStyles } from 'react-jss';

interface TransitionContainerProps {
  show: boolean;
  direction: 0 | 1;
  children: React.ReactNode;
}

const useStyles = createUseStyles({
  container: {
    position: 'relative',
    width: '100%',
  },
  content: {
    transition: 'transform 0.3s ease, opacity 0.3s ease',
    width: '100%',
  },
  entering: {
    opacity: 0,
    transform: (props: { direction: 0 | 1 }) =>
      props.direction === 0 ? 'translateX(50px)' : 'translateX(-50px)',
  },
  entered: {
    opacity: 1,
    transform: 'translateX(0)',
  },
  exiting: {
    opacity: 0,
    position: 'absolute',
    top: 0,
    left: 0,
    transform: (props: { direction: 0 | 1 }) =>
      props.direction === 0 ? 'translateX(-50px)' : 'translateX(50px)',
  },
});

const TransitionContainer: React.FC<TransitionContainerProps> = ({
  show,
  direction,
  children,
}) => {
  const [state, setState] = useState<
    'entering' | 'entered' | 'exiting' | 'exited'
  >(show ? 'entering' : 'exited');
  const classes = useStyles({ direction });
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;

    if (show && state !== 'entered') {
      setState('entering');
      timeoutId = setTimeout(() => {
        setState('entered');
      }, 50); // Small delay to ensure the transition starts
    } else if (!show && state !== 'exited') {
      setState('exiting');
      timeoutId = setTimeout(() => {
        setState('exited');
      }, 400); // Match the transition duration
    }

    if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
    timeoutIdRef.current = timeoutId;

    return () => {
      if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current);
    };
  }, [show, state]);

  if (state === 'exited') return null;

  return (
    <div className={classes.container}>
      <div className={`${classes.content} ${classes[state]}`}>{children}</div>
    </div>
  );
};

export default TransitionContainer;
